import {Helmet} from '@dr.pogodin/react-helmet';
import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {
  usePostSalesServiceOrderVariantSetAsActiveMutation,
  usePostSalesServiceOrderVariantSetAsDefaultMutation,
  usePostSalesServiceOrderVariantSetAsInactiveMutation,
  usePostSalesServiceOrderVariantUnsetAsDefaultMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const SALES_SERVICE_ORDER_VARIANT_GRID_CODE = 'sales-service-order-variant-config';

export function ServiceOrderVariants() {
  const navigate = useNavigate();

  const [postSalesServiceOrderVariantSetAsActive] =
    usePostSalesServiceOrderVariantSetAsActiveMutation();
  const [postSalesServiceOrderVariantSetAsInactive] =
    usePostSalesServiceOrderVariantSetAsInactiveMutation();
  const [postSalesServiceOrderVariantSetAsDefault] =
    usePostSalesServiceOrderVariantSetAsDefaultMutation();
  const [postSalesServiceOrderVariantUnsetAsDefault] =
    usePostSalesServiceOrderVariantUnsetAsDefaultMutation();

  const actionCallback: ActionCallback = (args) => {
    const id = isArray(args.rowId) ? args.rowId[0] : args.rowId;

    match(args.actionKey)
      .with('redirectDetail', () =>
        navigate(composePath(settingsRoutes.salesServiceOrderVariantsDetail, {params: {id}}))
      )
      .with('setAsActive', () =>
        postSalesServiceOrderVariantSetAsActive({body: {serviceOrderVariantIds: [id]}})
          .unwrap()
          .then(() => args.refreshData())
          .catch(handleApiError)
      )
      .with('setAsInactive', () =>
        postSalesServiceOrderVariantSetAsInactive({body: {serviceOrderVariantIds: [id]}})
          .unwrap()
          .then(() => args.refreshData())
          .catch(handleApiError)
      )
      .with('setAsDefault', () =>
        postSalesServiceOrderVariantSetAsDefault({serviceOrderVariantId: id})
          .unwrap()
          .then(() => args.refreshData())
          .catch(handleApiError)
      )
      .with('unsetAsDefault', () =>
        postSalesServiceOrderVariantUnsetAsDefault({serviceOrderVariantId: id})
          .unwrap()
          .then(() => args.refreshData())
          .catch(handleApiError)
      )
      .otherwise(showNotification.error);
  };

  return (
    <>
      <Helmet title={i18n.t('entity.order.labels.serviceOrderVariants')} />
      <SettingsTemplate
        header={{title: i18n.t('entity.order.labels.serviceOrderVariants')}}
        description={i18n.t('page.salesSettings.labels.serviceOrderVariantsDescription')}
        data-testid={testIds.settings.serviceOrderVariants('template')}
      >
        <DmsDataGrid
          gridCode={SALES_SERVICE_ORDER_VARIANT_GRID_CODE}
          actionCallback={actionCallback}
          data-testid={testIds.settings.serviceOrderVariants('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
